// Leads written before leadType existed are direct, as the app and the rules read them.
const leadTypeOf = (lead) => (lead.leadType === "nearby" ? "nearby" : "direct");

/**
 * Consolidate raw leads so that 1 customer + 1 property = 1 lead.
 * Merges historical duplicates, aggregates all actions taken (call, chat, visit),
 * calculates interaction count, and sorts by most recent activity timestamp.
 */
export function consolidateLeads(rawLeads) {
  if (!Array.isArray(rawLeads)) return [];

  const map = new Map();

  rawLeads.forEach((lead) => {
    // Group key: 1 customer + 1 property = 1 lead
    const groupKey = (lead.tenantId && lead.propertyId)
      ? `${lead.tenantId}_${lead.propertyId}`
      : lead.id;

    const existing = map.get(groupKey);
    if (!existing) {
      map.set(groupKey, {
        ...lead,
        leadType: leadTypeOf(lead),
        actions: Array.isArray(lead.actions) ? lead.actions : (lead.type ? [lead.type] : []),
        interactionCount: typeof lead.interactionCount === "number" ? lead.interactionCount : 1,
        sourceIds: [lead.id],
      });
    } else {
      const timeExisting = (existing.updatedAt || existing.createdAt)?.seconds || 0;
      const timeCurrent = (lead.updatedAt || lead.createdAt)?.seconds || 0;

      const latest = timeCurrent >= timeExisting ? lead : existing;
      const older = timeCurrent >= timeExisting ? existing : lead;

      const mergedActions = Array.from(new Set([
        ...(Array.isArray(latest.actions) ? latest.actions : [latest.type]),
        ...(Array.isArray(older.actions) ? older.actions : [older.type]),
      ])).filter(Boolean);

      // Same rule as the app's leadRepository: the latest doc's status wins,
      // except a fresh 'New' never downgrades a scheduled visit. (Forcing
      // 'Visit Scheduled' whenever any doc had it made the dealer's status
      // dropdown snap back — the lead could never be converted or closed.)
      let mergedStatus = latest.status || "New";
      if (older.status === "Visit Scheduled" && mergedStatus === "New") {
        mergedStatus = "Visit Scheduled";
      }

      const mergedCount = (latest.interactionCount || 1) + (older.interactionCount || 1);

      map.set(groupKey, {
        ...latest,
        leadType: leadTypeOf(latest),
        status: mergedStatus,
        actions: mergedActions,
        interactionCount: mergedCount,
        createdAt: older.createdAt || latest.createdAt,
        updatedAt: latest.updatedAt || latest.createdAt,
        // Every doc folded into this lead, so a status change can update all
        // of them rather than just the newest.
        sourceIds: [...(existing.sourceIds || [existing.id]), lead.id],
      });
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    const timeA = (a.updatedAt || a.createdAt)?.seconds || 0;
    const timeB = (b.updatedAt || b.createdAt)?.seconds || 0;
    return timeB - timeA;
  });
}
